<template>
    <!-- <div v-if="editable" class="bo-textarea-word-count"> -->
    <div class="bo-textarea-word-count">
        <textarea
            v-if="!chipVariables"
            ref="textarea"
            :class="['form-control', { 'has-variable': hasVariables }, { 'remark-form': isForm }]"
            v-model="dataInput"
            name="input"
            id="input"
            @input="onInput($event)"
            @click="updateCursorPosition"
            @keyup="updateCursorPosition"
            @focus="updateCursorPosition"
            @select="updateCursorPosition"
            @blur="onBlur($event)"
            :rows="rows"
            :disabled="disabled || !editable"
            :maxlength="maxlength"
        >
        </textarea>
        <!-- A textarea cannot render a label, so in chip mode the field is a contenteditable surface
             holding the same value (variables stay "{{var}}" text) with the tokens shown as chips. -->
        <div
            v-else
            ref="chipEditor"
            class="form-control chip-editor"
            :class="{ 'has-variable': hasVariables, 'remark-form': isForm }"
            :contenteditable="!disabled && editable"
            :style="chipEditorStyle"
            @input="syncChipEditor"
            @keydown="handleChipKeydown"
            @keyup="onChipCaretMove"
            @mouseup="onChipCaretMove"
            @paste="handleChipPaste"
            @blur="onBlur($event)"
        ></div>

        <!-- VARIABLE ROW -->
        <div
            class="variable-row"
            v-if="hasVariables"
        >
            <div class="title">
                <span>{{ generalTranslations.variable ?? "Variable" }}</span>
                <i
                    class="icon icon-info"
                    data-toggle="tooltip"
                    data-placement="bottom"
                    :title="generalTranslations.variable_insert_hint ?? 'Type &quot;/&quot; or click a tag to insert a variable.'"
                ></i>
            </div>

            <div class="variables-list">
                <template>
                    <div
                        v-for="variable in variables"
                        :key="variable.id"
                        class="variable cursor-pointer"
                        @mousedown.prevent
                        @click="insertVariable(variable)"
                    >
                        {{ variable.label }}
                    </div>
                </template>
            </div>
        </div>

        <label
            class="counter"
            v-if="characterCount"
        >
            <div
                v-if="limit !== null"
                :class="crossOverLimit ? 'text-danger' : ''"
            >
                {{ getCharacterCount }} / {{ limit }} {{ generalTranslations.character ?? "Characters" }}
            </div>
            <div v-else>{{ getCharacterCount }} {{ generalTranslations.character ?? "Characters" }}</div>
        </label>
        <label
            class="counter"
            v-if="wordCount"
        >
            <div :class="crossOverLimit ? 'text-danger' : ''">
                {{ getWordCount }} <span v-if="limit">/{{ limit }}</span> {{ generalTranslations.word ?? "Words" }}
            </div>
        </label>

        <!-- <div v-else style="white-space: pre;">{{ dataInput }}</div> -->

        <bo-variable-suggest
            v-if="suggestVisible"
            :items="suggestItems"
            :rect="suggestRect"
            :active-index="suggestIndex"
            @select="selectSuggestItem"
            @move="suggestIndex = $event"
        ></bo-variable-suggest>
    </div>
</template>

<script>
import { emit } from "process";
import {
    CARET_ANCHOR,
    createChipNode,
    elementToTokenText,
    ensureTrailingCaretAnchor,
    isCaretAnchor,
    matchVariableTrigger,
    tokenTextToFragment,
    VARIABLE_CHIP_CLASS,
} from "@/v2/utils/variable-token";
import BoVariableSuggest from "@/v2/components/bo-variable-suggest.vue";
import variableSuggestMixin from "@/v2/mixins/variable-suggest";

export default {
    components: {
        BoVariableSuggest,
    },
    mixins: [variableSuggestMixin],
    model: {
        prop: "value",
        event: "change",
    },
    props: {
        rows: {
            type: String,
            default: "4",
        },
        value: {
            type: String,
            default: "",
        },
        defaultValue: {
            type: String,
            default: "-",
        },
        variables: {
            type: Array,
            default: () => [],
        },
        // opt-in: render the variables as chips in a contenteditable field instead of "{{var}}" text.
        // Off by default so every other page using this component keeps its plain textarea.
        chipVariables: {
            type: Boolean,
            default: false,
        },
        // show sample values in the chips instead of the variable labels (the stored value is unchanged)
        previewSample: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        characterCount: {
            type: Boolean,
            default: false,
        },
        wordCount: {
            type: Boolean,
            default: false,
        },
        isForm: {
            type: Boolean,
            default: false,
        },
        limit: {
            type: Number,
            default: null,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        maxlength: {
            type: [Number, null],
            default: null,
        },
        delay: {
            type: [Number, null],
            default: null,
        },
    },

    data() {
        return {
            crossOverLimit: false,
            generalTranslations: window.bo?.generalTranslations ?? {},
            typingTimer: null,
            lastSavedValue: this.value ?? this.defaultValue,
            cursorStart: null,
            cursorEnd: null,
            lastEmittedValue: null,
            chipEditorStyle: "",
            // the "/query" range the suggest list is currently anchored to, replaced on selection
            suggestTokenRange: null,
        };
    },
    computed: {
        dataInput: {
            get() {
                return this.value ?? this.defaultValue;
            },
            set(newValue) {
                if (!this.disabled && this.editable) {
                    if (this.isOverLimit(newValue)) {
                        this.crossOverLimit = true;
                        return;
                    }

                    this.crossOverLimit = false;
                    this.$emit("change", newValue);
                    this.$emit("input", newValue);
                }
            },
        },
        getWordCount() {
            const words = this.dataInput.replace(/<\S[^><]*>/gi, "").match(/\w+/g);
            return words ? words.length : 0;
        },
        getCharacterCount() {
            return this.dataInput.length;
        },
        hasVariables: {
            get() {
                return this.variables.length;
            },
        },
        variablesKey() {
            return (this.variables ?? []).map((variable) => `${variable.id}:${variable.label}`).join("|");
        },
    },
    created() {
        // Sized like the textarea it replaces (rows lines tall), and set inline with !important because
        // this project clamps .form-control to 24px in places - which is exactly one line. A fixed
        // string rather than a reactive binding, so nothing re-applies the style while you type.
        const rows = Number(this.rows) || 4;

        this.chipEditorStyle = `display: block; height: auto; max-height: none !important; min-height: calc(${rows} * 1.5rem + 0.75rem + 2px) !important;`;
    },
    mounted() {
        if (this.chipVariables) {
            this.renderChipEditor(this.dataInput);
        }

        this.$nextTick(() => this.initTooltip());
    },
    updated() {
        this.initTooltip();
    },
    watch: {
        dataInput(newValue) {
            if (!this.chipVariables) {
                return;
            }

            // our own edit is already in the DOM; re-rendering here would move the caret
            if (newValue === this.lastEmittedValue) {
                return;
            }

            this.lastEmittedValue = newValue;
            this.renderChipEditor(newValue);
        },
        // keyed on the values, not the array identity: parents that build the list in a method hand
        // us a new array on every render, which would otherwise re-render the field and drop the caret
        variablesKey() {
            if (this.chipVariables) {
                this.renderChipEditor(this.dataInput);
            }
        },
        previewSample() {
            if (this.chipVariables) {
                this.renderChipEditor(this.dataInput);
            }
        },
    },
    methods: {
        // Bootstrap tooltips are imperative and the variable row is Vue-rendered, so initialise it
        // here. Guarded so a page-level $('[data-toggle="tooltip"]').tooltip() is not re-run.
        initTooltip() {
            if (typeof $ === "undefined" || typeof $.fn?.tooltip !== "function") {
                return;
            }

            const tooltipElement = this.$el.querySelector('[data-toggle="tooltip"]');

            if (tooltipElement && !$(tooltipElement).data("bs.tooltip")) {
                $(tooltipElement).tooltip();
            }
        },
        updateCursorPosition(event = null) {
            const textarea = event?.target ?? this.$refs.textarea;

            // chip mode edits a contenteditable surface, which has no selectionStart
            if (!textarea || typeof textarea.selectionStart !== "number") {
                return;
            }

            this.cursorStart = textarea.selectionStart;
            this.cursorEnd = textarea.selectionEnd;
        },
        insertVariable(variable) {
            if (this.disabled) {
                return;
            }

            if (this.chipVariables) {
                this.insertVariableChip(variable);
                return;
            }

            const variableText = `{{${variable.id}}}`;
            const value = this.dataInput ?? "";
            const start = Number.isInteger(this.cursorStart) ? this.cursorStart : value.length;
            const end = Number.isInteger(this.cursorEnd) ? this.cursorEnd : start;

            this.dataInput = `${value.slice(0, start)}${variableText}${value.slice(end)}`;

            this.$nextTick(() => {
                const textarea = this.$refs.textarea;
                const cursorPosition = start + variableText.length;

                if (!textarea) {
                    return;
                }

                textarea.focus();
                textarea.setSelectionRange(cursorPosition, cursorPosition);
                this.cursorStart = cursorPosition;
                this.cursorEnd = cursorPosition;
            });
        },
        // chip mode: insert an atomic chip at the caret, else at the end of the field
        insertVariableChip(variable) {
            const editor = this.$refs.chipEditor;

            if (!editor) {
                return;
            }

            // An empty contenteditable is held open by a lone <br>. It is not content, so drop it:
            // otherwise the chip lands before it and the caret ends up on the wrong side of it.
            if (elementToTokenText(editor) === "" && editor.firstChild) {
                editor.innerHTML = "";
            }

            const chip = createChipNode(variable, this.previewSample);
            // The label brings a caret anchor and a space with it, inserted as one unit so every branch
            // below gets all three: the anchor gives the caret a text position right after the atomic
            // chip, and the space separates it from the next word.
            const space = document.createTextNode(" ");
            const group = document.createDocumentFragment();
            group.appendChild(chip);
            group.appendChild(document.createTextNode(CARET_ANCHOR));
            group.appendChild(space);

            // capture the caret before focusing - focusing an unfocused field moves it to the start
            const selection = window.getSelection();
            const withinEditor = selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode);
            const savedRange = withinEditor ? selection.getRangeAt(0).cloneRange() : null;
            const caretChip = savedRange ? this.chipAncestor(savedRange.startContainer) : null;

            editor.focus();

            if (caretChip) {
                // the caret sat inside an existing chip - never nest one, put the new chip after it
                caretChip.parentNode.insertBefore(group, caretChip.nextSibling);
            } else if (savedRange) {
                savedRange.deleteContents();
                savedRange.insertNode(group);
            } else {
                editor.appendChild(group);
            }

            ensureTrailingCaretAnchor(editor);

            if (selection) {
                this.placeCaretAfter(space);
            }

            this.syncChipEditor();
        },
        // the caret moved without an input (arrow keys, a click) - re-evaluate the open suggest list
        onChipCaretMove() {
            this.snapCaretOutOfChip();
            this.updateSuggestFromSelection();
        },
        // a resize or scroll moved the caret under the open list - re-measure it
        refreshSuggestPosition() {
            this.updateSuggestFromSelection();
        },
        // Show, re-filter or dismiss the suggest list from the caret's position inside the field.
        updateSuggestFromSelection() {
            const editor = this.$refs.chipEditor;
            const selection = window.getSelection();

            this.suggestTokenRange = null;

            if (
                !this.chipVariables ||
                !editor ||
                !selection ||
                selection.rangeCount === 0 ||
                !selection.isCollapsed
            ) {
                this.closeSuggest();
                return;
            }

            const range = selection.getRangeAt(0);
            const node = range.startContainer;

            // the caret has to sit in the field's own text, not inside a chip
            if (node?.nodeType !== Node.TEXT_NODE || !editor.contains(node)) {
                this.closeSuggest();
                return;
            }

            const trigger = matchVariableTrigger(node.nodeValue.slice(0, range.startOffset));

            if (!trigger) {
                this.closeSuggest();
                return;
            }

            const rect = this.caretRect(node, range.startOffset);

            if (!rect) {
                this.closeSuggest();
                return;
            }

            const tokenRange = document.createRange();
            tokenRange.setStart(node, trigger.start);
            tokenRange.setEnd(node, range.startOffset);
            this.suggestTokenRange = tokenRange;

            this.openSuggest(trigger.query, rect);
        },
        // the caret's viewport position - { left, bottom } - measured from the field's own text
        caretRect(node, offset) {
            const range = document.createRange();
            range.setStart(node, offset);
            range.collapse(true);

            let rect = range.getBoundingClientRect();

            // a collapsed caret in a text node is not always measurable, so fall back to the char before
            if ((!rect || (!rect.top && !rect.bottom && !rect.left && !rect.right)) && offset > 0) {
                range.setStart(node, offset - 1);
                range.setEnd(node, offset);
                rect = range.getBoundingClientRect();
            }

            if (!rect) {
                return null;
            }

            return { left: rect.right, bottom: rect.bottom };
        },
        // a suggest option was chosen - swap the typed "/query" for the variable's chip
        applySuggestSelection(variable) {
            const editor = this.$refs.chipEditor;
            const range = this.suggestTokenRange;

            this.suggestTokenRange = null;

            if (!editor || !range) {
                return;
            }

            const chip = createChipNode(variable, this.previewSample);
            const space = document.createTextNode(" ");
            const group = document.createDocumentFragment();
            group.appendChild(chip);
            group.appendChild(document.createTextNode(CARET_ANCHOR));
            group.appendChild(space);

            range.deleteContents();
            range.insertNode(group);

            editor.focus();
            ensureTrailingCaretAnchor(editor);
            this.placeCaretAfter(space);
            this.syncChipEditor();
        },
        // the chip the node sits in, if any - the caret can land inside a chip
        chipAncestor(node) {
            const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;

            return element?.closest?.(`.${VARIABLE_CHIP_CLASS}`) ?? null;
        },
        // Put the caret directly after a node, preferring a real text position: a child-index range
        // next to an atomic inline-block is ambiguous, so a text node is used whenever one follows.
        placeCaretAfter(node) {
            const selection = window.getSelection();

            if (!selection || !node) {
                return;
            }

            const next = node.nextSibling;
            const caret = document.createRange();

            if (isCaretAnchor(next)) {
                // past the anchor, so typing appends after it and the zero-width char stays behind
                caret.setStart(next, next.nodeValue.length);
            } else if (next?.nodeType === Node.TEXT_NODE) {
                caret.setStart(next, 0);
            } else if (node.nodeType === Node.TEXT_NODE) {
                // the node just inserted is text - its end is a real, measurable caret position
                caret.setStart(node, node.nodeValue.length);
            } else {
                caret.setStartAfter(node);
            }

            caret.collapse(true);
            selection.removeAllRanges();
            selection.addRange(caret);
        },
        // a chip is atomic, so the caret belongs after it, never inside it
        snapCaretOutOfChip() {
            const editor = this.$refs.chipEditor;
            const selection = window.getSelection();

            if (!editor || !selection || selection.rangeCount === 0 || !selection.isCollapsed) {
                return;
            }

            const chip = this.chipAncestor(selection.getRangeAt(0).startContainer);

            if (!chip) {
                return;
            }

            this.placeCaretAfter(chip);
        },
        asChip(node) {
            return node?.nodeType === Node.ELEMENT_NODE && node.classList?.contains(VARIABLE_CHIP_CLASS) ? node : null;
        },
        placeCaretBefore(node) {
            const selection = window.getSelection();

            if (!selection || !node) {
                return;
            }

            const previous = node.previousSibling;
            const caret = document.createRange();

            if (previous?.nodeType === Node.TEXT_NODE) {
                caret.setStart(previous, previous.nodeValue.length);
            } else {
                caret.setStartBefore(node);
            }

            caret.collapse(true);
            selection.removeAllRanges();
            selection.addRange(caret);
        },
        removeChipWithCaret(editor, chip) {
            const previous = chip.previousSibling;
            chip.remove();

            if (previous) {
                this.placeCaretAfter(previous);
            } else {
                const selection = window.getSelection();
                const caret = document.createRange();
                caret.setStart(editor, 0);
                caret.collapse(true);
                selection.removeAllRanges();
                selection.addRange(caret);
            }

            this.syncChipEditor();
        },
        stepOverChip(key, editor, chip) {
            if (key === "Backspace" || key === "Delete") {
                this.removeChipWithCaret(editor, chip);
            } else if (key === "ArrowLeft") {
                this.placeCaretBefore(chip);
            } else {
                this.placeCaretAfter(chip);
            }
        },
        // A chip is atomic and the trailing caret anchor is invisible, so the editing keys have to step
        // over both as one unit. Left to the browser the caret lands on the zero-width char, which made
        // delete/backspace look broken.
        handleChipKeydown(event) {
            // the open suggest list owns the navigation keys before the chip-stepping logic runs
            if (this.handleSuggestKeydown(event)) {
                event.preventDefault();
                return;
            }

            if (!["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(event.key)) {
                return;
            }

            // modifier gestures - Option+Backspace (delete word), Cmd+Shift+Left (select to line start),
            // Cmd+A and friends - belong to the browser; touching the DOM mid-gesture breaks its caret
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
                return;
            }

            const editor = this.$refs.chipEditor;
            const selection = window.getSelection();

            if (!editor || !selection || selection.rangeCount === 0) {
                return;
            }

            const range = selection.getRangeAt(0);

            // a ranged selection belongs to the browser
            if (!range.collapsed) {
                return;
            }

            const chip = this.chipAncestor(range.startContainer);

            if (chip) {
                event.preventDefault();
                this.stepOverChip(event.key, editor, chip);
                return;
            }

            // the caret can also sit in the anchor right after a trailing chip
            const anchor = range.startContainer;

            if (!isCaretAnchor(anchor)) {
                return;
            }

            const chipBefore = this.asChip(anchor.previousSibling);

            if (!chipBefore) {
                return;
            }

            if (event.key === "Backspace") {
                event.preventDefault();
                anchor.remove();
                this.removeChipWithCaret(editor, chipBefore);
            } else if (event.key === "ArrowLeft") {
                event.preventDefault();
                this.placeCaretBefore(chipBefore);
            }
        },
        // the editor DOM -> the "{{var}}" text the component models and stores
        syncChipEditor() {
            const editor = this.$refs.chipEditor;

            if (!editor) {
                return;
            }

            // Nothing here may touch the DOM. Mutating it inside the input handler leaves the browser
            // painting the caret where it was before, until the next keystroke. The caret anchor only
            // has to be reconciled where we already reposition the caret (insert / render).
            const text = elementToTokenText(editor);
            const previous = this.dataInput;

            this.lastEmittedValue = text;
            this.dataInput = text;

            // the dataInput limit guard drops the change - put the field back so it matches the value
            if (this.isOverLimit(text)) {
                this.renderChipEditor(previous);
                this.closeSuggest();
            } else {
                this.updateSuggestFromSelection();
            }

            this.queueAutoSave();
        },
        isOverLimit(value) {
            if (this.wordCount) {
                const words = value.trim().split(/\s+/);

                return this.limit && words.length > this.limit;
            }

            if (this.characterCount) {
                return this.limit && value.length > this.limit;
            }

            return false;
        },
        renderChipEditor(text) {
            this.$nextTick(() => {
                const editor = this.$refs.chipEditor;

                if (!editor) {
                    return;
                }

                editor.innerHTML = "";
                editor.appendChild(tokenTextToFragment(text, this.variables, this.previewSample));
                ensureTrailingCaretAnchor(editor);
            });
        },
        handleChipPaste(event) {
            const editor = this.$refs.chipEditor;

            if (!editor) {
                return;
            }

            event.preventDefault();

            const text = event.clipboardData?.getData("text/plain") ?? "";

            if (!text) {
                return;
            }

            const selection = window.getSelection();
            const withinEditor = selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode);
            const fragment = tokenTextToFragment(text, this.variables, this.previewSample);

            if (withinEditor) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                range.insertNode(fragment);
                range.collapse(false);
            } else {
                editor.appendChild(fragment);
            }

            this.syncChipEditor();
        },
        onInput(event) {
            this.updateCursorPosition(event);
            this.queueAutoSave();
        },
        queueAutoSave() {
            if (this.delay === null) return;
            clearTimeout(this.typingTimer);

            this.typingTimer = setTimeout(() => {
                if (this.dataInput !== this.lastSavedValue) {
                    this.$emit("auto-save");
                    this.lastSavedValue = this.dataInput;
                }
            }, this.delay);
        },
        onBlur(event) {
            this.updateCursorPosition(event);
            // the field lost focus - e.g. a click elsewhere on the page - so drop the suggest list
            this.closeSuggest();
            clearTimeout(this.typingTimer);
            if (this.delay === null) {
                this.$emit("on-blur");
            } else {
                if (this.dataInput !== this.lastSavedValue) {
                    this.$emit("on-blur");
                    this.lastSavedValue = this.dataInput;
                }
            }
        },
    },

    // watch: {
    //     dataInput(newVal) {
    //         if (this.wordCount) {
    //             const words = newVal.trim().split(/\s+/);

    //             if (this.limit && words.length > this.limit) {
    //                 this.crossOverLimit = true;
    //             } else {
    //                 this.crossOverLimit = false;
    //             }
    //         } else if (this.characterCount) {
    //             if (this.limit && newVal.length > this.limit) {
    //                 this.crossOverLimit = true;
    //             } else {
    //                 this.crossOverLimit = false;
    //             }
    //         }
    //     },
    // },
};
</script>
